/* eslint-disable react-hooks/exhaustive-deps */
import { Autocomplete, CircularProgress } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CustomFormLabel, CustomTextField } from "../formsComponents";
import { debounce } from "lodash";
import { api } from "../../api/api";
import { useBillingCompanyContext } from "../../providers";

interface ICancellationReason {
  cancellation_reason_unique_id: number;
  cancellation_reason_uuid: string;
  name: string;
  description: string;
  master_cancellation_reason_uuid: string;
  company_outlet_uuid: string;
  status: string;
  created_by_uuid: string;
  created_by_name: string;
  modified_by_uuid: string;
  modified_by_name: string;
  create_ts: string | null;
  insert_ts: string;
}

interface ICancellationReasonAutoSearchProps {
  required: boolean;
  label?: string;
  value?: string;
  onSelect: (value: ICancellationReason | null) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  onBlur?: () => void;
}

export const CancellationReasonAutoSearch: React.FC<
  ICancellationReasonAutoSearchProps
> = (props) => {
  const {
    label,
    value,
    onSelect,
    disabled,
    error,
    helperText,
    placeholder,
    required,
    onBlur,
  } = props;
  const [options, setOptions] = useState<readonly ICancellationReason[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearchText] = useState<string>("");
  const [isFocused, setIsFocused] = useState(false);
  const initialLoad = useRef(false);
  const billingCompany = useBillingCompanyContext();
  const companyOutletUuid = billingCompany?.comapny?.company_outlet_uuid || "";

  const fetchSuggestion = async (enteredValue: string) => {
    if (!companyOutletUuid) return;

    setLoading(true);
    try {
      const response = await api.get(
        `/dataManagement/get-cancellation-reason?company_outlet_uuid=${companyOutletUuid}&status=ACTIVE`
      );
      const data: ICancellationReason[] = response.data.data;

      // Filter based on search text
      const filteredReasons =
        enteredValue.length > 0
          ? data.filter(
            (reason) =>
              reason.name
                .toLowerCase()
                .includes(enteredValue.toLowerCase()) ||
              reason.description
                .toLowerCase()
                .includes(enteredValue.toLowerCase())
          )
          : data;

      setOptions(filteredReasons);
    } catch (error) {
      console.error("Error fetching cancellation reasons:", error);
    } finally {
      setLoading(false);
    }
  };

  const debounceFn = useCallback(debounce(fetchSuggestion, 800), [
    companyOutletUuid,
  ]);

  const getOptionLabel = (option: string | ICancellationReason) => {
    if (typeof option === "string") {
      return option;
    }
    return option.name || "";
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (!initialLoad.current) {
      fetchSuggestion("");
      initialLoad.current = true;
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (onBlur) {
      onBlur();
    }
  };

  useEffect(() => {
    if (search) {
      debounceFn(search);
    }
  }, [search, debounceFn]);

  useEffect(() => {
    if (value && !initialLoad.current && companyOutletUuid) {
      fetchSuggestion("");
      initialLoad.current = true;
    }
  }, [value, companyOutletUuid]);

  const displayOptions = useMemo(() => {
    let finalOptions = [...options];

    if (value && typeof value === "string" && value.length > 0) {
      const exists = finalOptions.some(
        (opt) =>
          opt.cancellation_reason_uuid === value ||
          opt.name.toLowerCase() === value.toLowerCase()
      );

      if (!exists) {
        finalOptions = [
          {
            cancellation_reason_unique_id: 0,
            cancellation_reason_uuid: "__PREFILLED__",
            name: value,
            description: "",
            master_cancellation_reason_uuid: "",
            company_outlet_uuid: companyOutletUuid,
            status: "ACTIVE",
            created_by_uuid: "",
            created_by_name: "",
            modified_by_uuid: "",
            modified_by_name: "",
            create_ts: null,
            insert_ts: "",
          },
          ...finalOptions,
        ];
      }
    }

    return finalOptions;
  }, [options, value, companyOutletUuid]);

  const getAutocompleteValue = () => {
    if (!value) return null;

    return (
      displayOptions.find(
        (option) =>
          option.cancellation_reason_uuid === value ||
          option.name.toLowerCase() === value.toLowerCase()
      ) || null
    );
  };

  const selectedValueText = useMemo(() => {
    const selectedOption = getAutocompleteValue();
    if (selectedOption && typeof selectedOption === "object") {
      return selectedOption.name || "";
    }
    return "";
  }, [value, displayOptions]);

  return (
    <>
      <Autocomplete
        id="cancellation-reason-auto-search"
        fullWidth
        freeSolo
        sx={{
          ".MuiOutlinedInput-root": {
            paddingTop: "2px",
            paddingBottom: "2px",
            fontSize: "0.8rem",
            color: "rgb(38, 38, 38)",
            width: "100%",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: error ? "red" : undefined,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: error ? "red" : undefined,
            },
          },
        }}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, value) =>
          typeof option !== "string" && typeof value !== "string"
            ? option.cancellation_reason_uuid === value.cancellation_reason_uuid
            : option === value
        }
        filterOptions={(x) => x}
        disabled={disabled}
        options={displayOptions}
        onFocus={handleFocus}
        onOpen={() => setIsFocused(true)}
        onClose={() => {
          if (!selectedValueText) {
            setIsFocused(false);
          }
        }}
        includeInputInList
        value={getAutocompleteValue()}
        loading={loading}
        noOptionsText="No cancellation reasons found"
        onChange={(
          event,
          selectedValue: string | ICancellationReason | null
        ) => {
          if (typeof selectedValue === "object" && selectedValue !== null) {
            onSelect(selectedValue);
          } else {
            onSelect(null);
          }
        }}
        onInputChange={(event, newInputValue) => {
          if ((event && event.type === "change") || !newInputValue) {
            setSearchText(newInputValue);
          }
        }}
        renderInput={(params) => {
          const inputValue = params.inputProps?.value;
          const inputValueStr = typeof inputValue === "string" ? inputValue : String(inputValue || "");
          const hasValue = selectedValueText.length > 0 || inputValueStr.length > 0;
          const shouldShrink = hasValue || isFocused;

          return (
            <CustomTextField
              label={label}
              {...params}
              fullWidth
              placeholder={placeholder}
              error={error}
              helperText={helperText}
              required={required}
              onFocus={(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                setIsFocused(true);
                if (params.inputProps?.onFocus) {
                  params.inputProps.onFocus(e as any);
                }
              }}
              onBlur={(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                handleBlur();
                if (params.inputProps?.onBlur) {
                  params.inputProps.onBlur(e as any);
                }
              }}
              InputLabelProps={{
                ...params.InputLabelProps,
                shrink: shouldShrink,
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          );
        }}
      />
    </>
  );
};